/**
 * Generic HTTP API client.
 *
 * Base service for REST calls with configurable base URL. Use for backend or APIM-backed APIs. Authentication is
 * handled by auth interceptors.
 */

import { HttpClient, HttpContext, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { API_BASE_URL } from './api-client.tokens';

export interface RequestOptions {
  params?: HttpParams | { [key: string]: string | string[] };
  headers?: HttpHeaders | { [key: string]: string | string[] };
  context?: HttpContext;
  reportProgress?: boolean;
  responseType?: 'json' | 'text' | 'blob' | 'arraybuffer';
}

@Injectable()
export class ApiClientService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = inject(API_BASE_URL, { optional: true }) ?? '';

  /** GET request */
  get<T>(path: string, options?: RequestOptions): Observable<T> {
    const url = this.buildUrl(path);
    return this.http.get<T>(url, {
      params: options?.params,
      headers: options?.headers,
      context: options?.context,
      reportProgress: options?.reportProgress,
      responseType: (options?.responseType as 'json') ?? 'json',
    });
  }

  /** POST request */
  post<T>(path: string, body: unknown, options?: RequestOptions): Observable<T> {
    const url = this.buildUrl(path);
    return this.http.post<T>(url, body, {
      params: options?.params,
      headers: options?.headers,
      context: options?.context,
      reportProgress: options?.reportProgress,
      responseType: (options?.responseType as 'json') ?? 'json',
    });
  }

  /** PUT request */
  put<T>(path: string, body: unknown, options?: RequestOptions): Observable<T> {
    const url = this.buildUrl(path);
    return this.http.put<T>(url, body, {
      params: options?.params,
      headers: options?.headers,
      context: options?.context,
      reportProgress: options?.reportProgress,
      responseType: (options?.responseType as 'json') ?? 'json',
    });
  }

  /** PATCH request */
  patch<T>(path: string, body: unknown, options?: RequestOptions): Observable<T> {
    const url = this.buildUrl(path);
    return this.http.patch<T>(url, body, {
      params: options?.params,
      headers: options?.headers,
      context: options?.context,
      reportProgress: options?.reportProgress,
      responseType: (options?.responseType as 'json') ?? 'json',
    });
  }

  /** DELETE request */
  delete<T>(path: string, options?: RequestOptions): Observable<T> {
    const url = this.buildUrl(path);
    return this.http.delete<T>(url, {
      params: options?.params,
      headers: options?.headers,
      context: options?.context,
      reportProgress: options?.reportProgress,
      responseType: (options?.responseType as 'json') ?? 'json',
    });
  }

  private buildUrl(path: string): string {
    const base = this.baseUrl.replace(/\/+$/, '');
    const segment = path.startsWith('/') ? path : `/${path}`;
    return `${base}${segment}`;
  }
}
